import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, X, Barbell, DotsSixVertical, Link as LinkIcon } from "@phosphor-icons/react";
import axios from "axios";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const days = ["Lunedi", "Martedi", "Mercoledi", "Giovedi", "Venerdi", "Sabato", "Domenica"];
const SUPERSET_LETTERS = ["A", "B", "C", "D", "E", "F"];

function ExerciseAutocomplete({ value, onChange, index }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/exercises/search?q=${encodeURIComponent(query)}`);
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    setActiveIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
  };

  const selectSuggestion = (name) => {
    onChange(name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex].name);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <Input
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        onKeyDown={handleKeyDown}
        placeholder="Nome esercizio"
        className="bg-transparent border-[#262626] text-white text-sm"
        autoComplete="off"
        data-testid={`exercise-name-${index}`}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#333] rounded-lg shadow-xl overflow-hidden" data-testid={`exercise-suggestions-${index}`}>
          {suggestions.map((s, i) => (
            <button
              key={s.name + i}
              onClick={() => selectSuggestion(s.name)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                i === activeIndex ? "bg-[#007AFF]/15 text-white" : "text-zinc-300 hover:bg-white/5"
              }`}
              data-testid={`suggestion-${index}-${i}`}
            >
              <Barbell size={13} className="text-zinc-500 flex-shrink-0" />
              <span className="flex-1">{s.name}</span>
              {s.muscle_group && <span className="text-[10px] text-zinc-600">{s.muscle_group}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortableExerciseRow({ ex, index, sortId, updateExercise, removeExercise, supersetOptions }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: sortId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 bg-[#0A0A0A] rounded-lg border ${isDragging ? "border-[#007AFF]/50 shadow-lg shadow-[#007AFF]/10" : "border-[#1A1A1A]"}`}
      data-testid={`exercise-row-${index}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <button
          {...attributes}
          {...listeners}
          className="text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
          data-testid={`drag-exercise-${index}`}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
        <ExerciseAutocomplete
          value={ex.name}
          onChange={(val) => updateExercise(index, "name", val)}
          index={index}
        />
        <div className="flex-shrink-0 flex items-center gap-1" title="Raggruppa in superset: gli esercizi con la stessa lettera consecutivi vengono eseguiti uno dopo l'altro senza recupero">
          <LinkIcon size={13} className={ex.superset_id ? "text-[#AF52DE]" : "text-zinc-600"} />
          <select
            value={ex.superset_id || ""}
            onChange={(e) => updateExercise(index, "superset_id", e.target.value)}
            className={`bg-[#0A0A0A] border border-[#262626] text-xs rounded h-7 px-1.5 cursor-pointer hover:border-[#333] focus:outline-none focus:border-[#AF52DE]/60 ${
              ex.superset_id ? "text-[#AF52DE] font-bold" : "text-zinc-500"
            }`}
            data-testid={`exercise-superset-${index}`}
          >
            <option value="">—</option>
            {supersetOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <button onClick={() => removeExercise(index)} className="text-zinc-600 hover:text-red-400 flex-shrink-0" data-testid={`remove-exercise-${index}`}><X size={16} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2 pl-6">
        <div>
          <span className="text-[10px] text-zinc-600 uppercase">Serie</span>
          <Input type="number" value={ex.sets} onChange={(e) => updateExercise(index, "sets", e.target.value)} className="bg-transparent border-[#262626] text-white text-sm h-8" data-testid={`exercise-sets-${index}`} />
        </div>
        <div>
          <span className="text-[10px] text-zinc-600 uppercase">Ripetizioni</span>
          <Input value={ex.reps} onChange={(e) => updateExercise(index, "reps", e.target.value)} className="bg-transparent border-[#262626] text-white text-sm h-8" data-testid={`exercise-reps-${index}`} />
        </div>
        <div>
          <span className="text-[10px] text-zinc-600 uppercase">Recupero (s)</span>
          <Input type="number" value={ex.rest_seconds} onChange={(e) => updateExercise(index, "rest_seconds", e.target.value)} className="bg-transparent border-[#262626] text-white text-sm h-8" data-testid={`exercise-rest-${index}`} />
        </div>
      </div>
      <div className="mt-2 pl-6">
        <span className="text-[10px] text-zinc-600 uppercase tracking-wider flex items-center gap-1">
          Nota per il cliente
          <span className="text-[9px] text-zinc-700 normal-case">(specifica di questa assegnazione)</span>
        </span>
        <Textarea
          value={ex.notes || ""}
          onChange={(e) => updateExercise(index, "notes", e.target.value)}
          placeholder="Es. Concentrati sulla tecnica, carico leggero questa settimana..."
          className="mt-1 bg-transparent border-[#262626] text-white text-sm min-h-[52px] resize-none placeholder:text-zinc-600"
          data-testid={`exercise-notes-${index}`}
        />
      </div>
    </div>
  );
}

export default function WorkoutDialog({
  open, onOpenChange, editing, form, setForm,
  programs, phases, onSave,
}) {
  // Generate stable IDs for sortable items
  const [exerciseIds, setExerciseIds] = useState([]);

  useEffect(() => {
    // Sync IDs when exercises are added/removed externally. Reordering is
    // handled explicitly in handleDragEnd, so we deliberately depend only on
    // the array LENGTH (not the array reference) to avoid spurious re-syncs
    // on every parent re-render.
    setExerciseIds((prev) => {
      if (prev.length === form.exercises.length) return prev;
      const ids = form.exercises.map((_, i) => prev[i] || `ex-${Date.now()}-${i}-${Math.random()}`);
      return ids;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.exercises.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const addExercise = () => {
    const newId = `ex-${Date.now()}-${Math.random()}`;
    setExerciseIds((prev) => [...prev, newId]);
    setForm({
      ...form,
      exercises: [...form.exercises, { name: "", sets: 3, reps: "10", rest_seconds: 60, notes: "", superset_id: "" }],
    });
  };

  const updateExercise = (index, field, value) => {
    const updated = [...form.exercises];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, exercises: updated });
  };

  const removeExercise = (index) => {
    setExerciseIds((prev) => prev.filter((_, i) => i !== index));
    setForm({ ...form, exercises: form.exercises.filter((_, i) => i !== index) });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = exerciseIds.indexOf(active.id);
    const newIndex = exerciseIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setExerciseIds(arrayMove(exerciseIds, oldIndex, newIndex));
    setForm({ ...form, exercises: arrayMove([...form.exercises], oldIndex, newIndex) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="workout-dialog">
        <DialogHeader>
          <DialogTitle className="font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {editing ? "MODIFICA SCHEDA" : "NUOVA SCHEDA"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="workout-title" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Giorno</Label>
              <select
                value={form.day_of_week}
                onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-md px-3 py-2 text-sm"
                data-testid="workout-day"
              >
                <option value="">-</option>
                {days.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Programma</Label>
              <Select value={form.program_id || "none"} onValueChange={(v) => setForm({ ...form, program_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="workout-program-select">
                  <SelectValue placeholder="Nessun programma" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="none" className="text-white">Nessun programma</SelectItem>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-white">{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fase</Label>
              <Select value={form.phase_id || "none"} onValueChange={(v) => setForm({ ...form, phase_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="workout-phase-select">
                  <SelectValue placeholder="Nessuna fase" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="none" className="text-white">Nessuna fase</SelectItem>
                  {phases.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-white">{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrizione</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white min-h-[60px] resize-none" data-testid="workout-description" />
          </div>

          {/* Exercises with DnD */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Esercizi</Label>
              <Button type="button" onClick={addExercise} variant="outline" size="sm" className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A] h-7 text-xs" data-testid="add-exercise-button">
                <Plus size={14} className="mr-1" /> Esercizio
              </Button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={exerciseIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {form.exercises.map((ex, i) => (
                    <SortableExerciseRow
                      key={exerciseIds[i] || `fallback-${i}`}
                      sortId={exerciseIds[i] || `fallback-${i}`}
                      ex={ex}
                      index={i}
                      updateExercise={updateExercise}
                      removeExercise={removeExercise}
                      supersetOptions={SUPERSET_LETTERS}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]" data-testid="workout-cancel">Annulla</Button>
          <Button onClick={onSave} className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="workout-save">{editing ? "Aggiorna" : "Crea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
